import { call } from 'redux-saga/effects'
import { HORIZON_SHADING_PROVIDERS } from 'resources/projectConfigurations/ProjectConfigurations'

interface HorizonProvider {
  id: number
  name: string
}

export enum HorizonFetchStatus {
  Success = 'success',
  InvalidCoordinates = 'invalid-coordinates',
  MissingProvider = 'missing-provider',
  Error = 'error',
}

export interface HorizonDataParams {
  horizon?: number[]
  loadingIssues?: 'error' | 'missingValues' | null
}

type Coordinates = [number, number]
type ApiResponse = {
  file_contents: string
  status: 'success' | 'error'
}

const getProviderInfo = (provider: string): HorizonProvider => {
  const providerInfo = HORIZON_SHADING_PROVIDERS.find((p) => p.id === parseInt(provider))
  if (!providerInfo) {
    throw new Error('Unable to find shading provider')
  }
  return providerInfo
}

const buildHorizonUrl = (coordinates: Coordinates, provider: string, orgId: string): string => {
  const [lon, lat] = coordinates
  const params = new URLSearchParams({
    lon: String(lon),
    lat: String(lat),
    provider,
    pre_warm: String(false),
  })
  return `${window.API_ROOT}/api/orgs/${orgId}/horizon/retrieve_or_store/?${params}`
}

const validateCoordinates = (coordinates: Coordinates): boolean => {
  const [lon, lat] = coordinates
  return typeof lat === 'number' && typeof lon === 'number'
}

const getConfigurationData = () => {
  const orgId = window.reduxStore.getState().auth.org_id
  const shadingProvider = window.projectForm.getState().values?.configuration?.horizon_shading_provider
  return { orgId, shadingProvider }
}

const validateHorizonData = ({ horizon, loadingIssues }: HorizonDataParams): number[] => {
  if (loadingIssues === 'error') {
    throw new Error('Invalid horizon data format')
  }

  if (loadingIssues === 'missingValues') {
    throw new Error('Incomplete horizon data: less than 360 values provided')
  }

  if (!horizon || !Array.isArray(horizon) || horizon.length !== 360) {
    throw new Error('Invalid horizon data structure')
  }

  return horizon
}

const processHorizonData = (data: ApiResponse, providerName: string): boolean => {
  const horizonData = window.CompressionHelper.decompress(data.file_contents)
  const validatedHorizonData = validateHorizonData(
    window.SceneHelper.loadHorizonDataFromFileContents(horizonData) as HorizonDataParams
  )
  return window.ShadeHelper.checkSceneAndSetupHorizonObject(providerName, validatedHorizonData)
}

function* fetchAndStoreHorizonData(coordinates: Coordinates): Generator<any, HorizonFetchStatus, any> {
  try {
    if (!validateCoordinates(coordinates)) {
      return HorizonFetchStatus.InvalidCoordinates
    }

    const { orgId, shadingProvider } = getConfigurationData()
    if (!shadingProvider) {
      return HorizonFetchStatus.MissingProvider
    }

    const providerInfo = getProviderInfo(shadingProvider)
    const url = buildHorizonUrl(coordinates, shadingProvider, orgId)

    const response: Response = yield call(fetch, url, {
      headers: window.Utils.tokenAuthHeaders(),
    })

    if (!response.ok) {
      throw new Error(`Fetching Horizon Data failed - status: ${response.status}`)
    }

    const data: ApiResponse = yield call([response, 'json'])
    if (!data?.file_contents || data.status !== 'success') {
      throw new Error('Failed to generate/fetch horizon shading data')
    }

    const processed = processHorizonData(data, providerInfo.name)
    return processed ? HorizonFetchStatus.Success : HorizonFetchStatus.Error
  } catch (error) {
    console.error('Error fetching horizon data:', error)
    return HorizonFetchStatus.Error
  }
}

export const horizonService = {
  fetchAndStoreHorizonData,
}
